'use client';

import * as SwitchPrimitive from '@radix-ui/react-switch';
import * as React from 'react';

import { cn } from '@/lib/ui-kit/utils';

interface SwitchProps extends React.ComponentProps<typeof SwitchPrimitive.Root> {
  label?: React.ReactNode;
}

function Switch({ className, label, id, disabled, ...props }: SwitchProps) {
  const autoId = React.useId();
  const switchId = id ?? (label ? autoId : undefined);

  return (
    <div className={cn('flex items-center gap-2', disabled && 'cursor-not-allowed opacity-50')}>
      <SwitchPrimitive.Root
        data-slot="switch"
        id={switchId}
        disabled={disabled}
        className={cn(
          'peer relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-[6px]',
          'bg-v2-text-secondary outline-none',
          'transition-colors duration-200',
          // hover: grey-400 — overridden by checked state (attr > pseudo specificity)
          'hover:bg-v2-btn-secondary-hover',
          'data-[state=checked]:bg-primary',
          'focus-visible:ring-2 focus-visible:ring-ring/50',
          'disabled:cursor-not-allowed',
          className,
        )}
        {...props}
      >
        <SwitchPrimitive.Thumb
          className={cn(
            'pointer-events-none block size-4 shrink-0 rounded-[6px] bg-white',
            'shadow-[0px_2px_4px_rgba(39,39,39,0.1)]',
            'transition-transform duration-200 ease-in-out',
            'translate-x-[2px] data-[state=checked]:translate-x-[18px]',
          )}
        />
      </SwitchPrimitive.Root>

      {label && (
        <label
          htmlFor={switchId}
          className={cn(
            'cursor-pointer select-none text-[13px] font-normal leading-[1.5]',
            'text-v2-text-secondary',
            'peer-data-[state=checked]:text-v2-btn-secondary-text',
          )}
        >
          {label}
        </label>
      )}
    </div>
  );
}

export type { SwitchProps };
export { Switch };
